import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';

import config from '../twh/config';
import { getFirstErrorMessage } from '../twh/functions';

const GET_API = `${config.reactApi_baseUrl}/react-api/color-scheme/color-scheme.php`;
const SAVE_API = `${config.reactApi_baseUrl}/react-api/color-scheme/add-edit-color-scheme.php`;
const UPLOAD_FONT_API = `${config.reactApi_baseUrl}/react-api/color-scheme/upload-font.php`;
const DELETE_FONT_API = `${config.reactApi_baseUrl}/react-api/color-scheme/delete-font.php`;

export default function ColorSchemePage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingFonts, setUploadingFonts] = useState(false);

  const [errorMsg, setErrorMsg] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fontToDelete, setFontToDelete] = useState(null);

  const [formData, setFormData] = useState({
    font_primary: null,
    font_primary_name: '',
    font_secondary: null,
    font_secondary_name: '',

    background_primary: '#c2edff',
    background_secondary: '#ebf1ff',

    heading_color: '#4f4f4f',
    heading_direction: 'text-align:left !important;',
    heading_style: 'font-weight:normal !important;',
    heading_size: 25,

    subheading_color: '#a3a3a3',
    subheading_direction: 'text-align:left !important;',
    subheading_style: 'font-weight:normal !important;',
    subheading_size: 18,

    paragraph_color: '#858585',
    paragraph_direction: 'text-align:left !important;',
    paragraph_style: 'font-weight:normal !important;',
    paragraph_size: 14,

    button_background: '#E5E5EB',
    button_text_color: '#888888',
    button_hover_background: '#888888',
    button_hover_text_color: '#ffffff',
    button_style: 'font-weight:normal !important;',
    button_text_direction: 'text-align:center !important;',
    button_padding: 15,
    button_text_size: 14,
  });

  useEffect(() => {
    loadColorScheme();
  }, []);

  const loadColorScheme = async () => {
    setLoading(true);
    setErrorMsg(null);

    try {
      const res = await fetch(GET_API, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch color scheme');

      const json = await res.json();

      if (json.status === true && json.data?.length > 0) {
        const d = json.data[0];

        setFormData({
          font_primary: null,
          font_primary_name: d.font_primary || '',
          font_secondary: null,
          font_secondary_name: d.font_secondary || '',

          background_primary: d.background_primary || '#c2edff',
          background_secondary: d.background_secondary || '#ebf1ff',

          heading_color: d.heading_color || '#4f4f4f',
          heading_direction: d.heading_direction || 'text-align:left !important;',
          heading_style: d.heading_style || 'font-weight:normal !important;',
          heading_size: Number(d.heading_size) || 25,

          subheading_color: d.subheading_color || '#a3a3a3',
          subheading_direction: d.subheading_direction || 'text-align:left !important;',
          subheading_style: d.subheading_style || 'font-weight:normal !important;',
          subheading_size: Number(d.subheading_size) || 18,

          paragraph_color: d.paragraph_color || '#858585',
          paragraph_direction: d.paragraph_direction || 'text-align:left !important;',
          paragraph_style: d.paragraph_style || 'font-weight:normal !important;',
          paragraph_size: Number(d.paragraph_size) || 14,

          button_background: d.button_background || '#E5E5EB',
          button_text_color: d.button_text_color || '#888888',
          button_hover_background: d.button_hover_background || '#888888',
          button_hover_text_color: d.button_hover_text_color || '#ffffff',
          button_style: d.button_style || 'font-weight:normal !important;',
          button_text_direction: d.button_text_direction || 'text-align:center !important;',
          button_padding: Number(d.button_padding) || 15,
          button_text_size: Number(d.button_text_size) || 14,
        });
      }
    } catch (err) {
      setErrorMsg('Failed to load color scheme settings.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSliderChange = (field) => (_, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (field, nameField) => (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        [field]: file,
        [nameField]: file.name,
      }));
    }
  };

  const openDeleteDialog = (fontField) => {
    setFontToDelete(fontField);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setFontToDelete(null);
  };

  const confirmDeleteFont = async () => {
    if (!fontToDelete) return;
    closeDeleteDialog();

    try {
      const fd = new FormData();
      fd.append('issetFontDelete', '1');
      fd.append('fontColName', fontToDelete);

      const res = await fetch(DELETE_FONT_API, {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });

      const json = await res.json();

      if (json.status === true) {
        setFormData((prev) => ({
          ...prev,
          [`${fontToDelete}_name`]: '',
          [`${fontToDelete}`]: null,
        }));
        setSuccessMsg('Font deleted successfully.');
      } else {
        setErrorMsg(getFirstErrorMessage(json.errorMsg) || 'Failed to delete font.');
      }
    } catch (err) {
      setErrorMsg('Network error while deleting font.');
      console.error(err);
    }
  };

  const uploadFonts = async () => {
    if (!formData.font_primary && !formData.font_secondary) {
      setErrorMsg('No font file selected.');
      return;
    }

    setUploadingFonts(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    const fd = new FormData();
    fd.append('issetFontUpload', '1');

    if (formData.font_primary) fd.append('font_primary', formData.font_primary);
    if (formData.font_secondary) fd.append('font_secondary', formData.font_secondary);

    try {
      const res = await fetch(UPLOAD_FONT_API, {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });

      const json = await res.json();

      if (json.status === true) {
        setSuccessMsg('Fonts uploaded successfully.');
        loadColorScheme(); // refresh names & clear file inputs
      } else {
        setErrorMsg(getFirstErrorMessage(json.errorMsg) || 'Font upload failed.');
      }
    } catch (err) {
      setErrorMsg('Network error during font upload.');
      console.error(err);
    } finally {
      setUploadingFonts(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    const fd = new FormData();
    fd.append('issetUpdateCssStyle', 1);
    // Explicitly list all fields we want to send (better control & readability)
    const fieldsToSend = [
      'background_primary',
      'background_secondary',
      'heading_color',
      'heading_direction',
      'heading_style',
      'heading_size',
      'subheading_color',
      'subheading_direction',
      'subheading_style',
      'subheading_size',
      'paragraph_color',
      'paragraph_direction',
      'paragraph_style',
      'paragraph_size',
      'button_background',
      'button_text_color',
      'button_hover_background',
      'button_hover_text_color',
      'button_style',
      'button_text_direction',
      'button_padding',
      'button_text_size',
    ];

    fieldsToSend.forEach((key) => {
      fd.append(key, formData[key]);
    });

    try {
      const res = await fetch(SAVE_API, {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });

      const json = await res.json();

      if (json.status === true) {
        setSuccessMsg('Settings saved successfully!');
        loadColorScheme(); // refresh everything
      } else {
        setErrorMsg(getFirstErrorMessage(json.errorMsg) || 'Failed to save settings.');
      }
    } catch (err) {
      setErrorMsg('Network error while saving settings.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight={800} mb={3}>
        COLOR SCHEME
      </Typography>

      {errorMsg && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMsg(null)}>
          {errorMsg}
        </Alert>
      )}

      {successMsg && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMsg(null)}>
          {successMsg}
        </Alert>
      )}

      {/* FONTS SECTION */}
      <Paper elevation={4} sx={{ p: 4, mb: 3, borderRadius: 3 }}>
        <Typography variant="h5" fontWeight={700} color="#FF2C6D" mb={3}>
          Fonts
        </Typography>

        <Grid container spacing={3}>
          <Grid item size={{xs:12, md:6}}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
              Primary Font (Headings)
            </Typography>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={<CloudUploadIcon />}
              sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
            >
              Choose file (.ttf, .otf)
              <input
                type="file"
                hidden
                accept=".ttf,.otf"
                onChange={handleFileChange('font_primary', 'font_primary_name')}
              />
            </Button>

            {formData.font_primary_name && (
              <Box mt={1.5} display="flex" alignItems="center" gap={2}>
                <Typography variant="body2" color="text.secondary">
                  {formData.font_primary_name}
                </Typography>
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => openDeleteDialog('font_primary')}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Grid>

          <Grid item size={{xs:12, md:6}}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
              Secondary Font (Body, Subheadings, etc.)
            </Typography>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={<CloudUploadIcon />}
              sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
            >
              Choose file (.ttf, .otf)
              <input
                type="file"
                hidden
                accept=".ttf,.otf"
                onChange={handleFileChange('font_secondary', 'font_secondary_name')}
              />
            </Button>

            {formData.font_secondary_name && (
              <Box mt={1.5} display="flex" alignItems="center" gap={2}>
                <Typography variant="body2" color="text.secondary">
                  {formData.font_secondary_name}
                </Typography>
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => openDeleteDialog('font_secondary')}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Grid>
        </Grid>

        <Box textAlign="right" mt={4}>
          <Button
            variant="contained"
            sx={{ bgcolor: '#FF2C6D', '&:hover': { bgcolor: '#ad1457' } }}
            onClick={uploadFonts}
            disabled={uploadingFonts}
            startIcon={uploadingFonts ? <CircularProgress size={20} /> : <CloudUploadIcon />}
          >
            {uploadingFonts ? 'Uploading...' : 'Upload Fonts'}
          </Button>
        </Box>
      </Paper>

      {/* MAIN SETTINGS FORM */}
      <Paper elevation={4} sx={{ p: 4, borderRadius: 3 }} component="form" onSubmit={handleSave}>
        <Grid container justifyContent="center">
          <Grid item size={{xs:12, md:8}}>
            <Typography variant="h5" fontWeight={700} color="#FF2C6D" mb={3}>
              Background
            </Typography>
            
            

            <Grid container spacing={3} mb={6}>
              {['primary', 'secondary'].map((type) => (
              <Grid item size={{xs:12, md:6}} key={type}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        border: '3px solid #eee',
                        bgcolor: formData[`background_${type}`],
                        position: 'relative',
                        boxShadow: 1,
                      }}
                    >
                      <input
                        type="color"
                        name={`background_${type}`}
                        value={formData[`background_${type}`]}
                        onChange={handleChange}
                        style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2">Background {type.charAt(0).toUpperCase() + type.slice(1)}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {type === 'primary' ? 'Main / Dark' : 'Light / Secondary'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 5 }} />

            {['heading', 'subheading', 'paragraph'].map((section) => (
              <React.Fragment key={section}>
                <Typography variant="h5" fontWeight={700} color="#FF2C6D" mb={2}>
                  {section.charAt(0).toUpperCase() + section.slice(1)}
                </Typography>

                <Grid container spacing={3} mb={3}>
                  <Grid item size={{xs:12, md:6}}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: '50%',
                          border: '3px solid #eee',
                          bgcolor: formData[`${section}_color`],
                          position: 'relative',
                        }}
                      >
                        <input
                          type="color"
                          name={`${section}_color`}
                          value={formData[`${section}_color`]}
                          onChange={handleChange}
                          style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                        />
                      </Box>
                      <Typography variant="subtitle2">Text Color</Typography>
                    </Box>
                  </Grid>

                  <Grid item size={{xs:6, md:3}}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Alignment</InputLabel>
                      <Select
                        name={`${section}_direction`}
                        value={formData[`${section}_direction`]}
                        onChange={handleChange}
                      >
                        <MenuItem value="text-align:left !important;">Left</MenuItem>
                        <MenuItem value="text-align:center !important;">Center</MenuItem>
                        <MenuItem value="text-align:right !important;">Right</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item size={{xs:6, md:3}}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Style</InputLabel>
                      <Select
                        name={`${section}_style`}
                        value={formData[`${section}_style`]}
                        onChange={handleChange}
                      >
                        <MenuItem value="font-weight:normal !important;">Normal</MenuItem>
                        <MenuItem value="font-weight:bold !important;">Bold</MenuItem>
                        <MenuItem value="font-style:italic !important;">Italic</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid item size={{xs:12, md:12}}>
                    <Typography variant="subtitle2" mb={1}>
                      Font Size ({formData[`${section}_size`]}px)
                    </Typography>
                    <Slider
                      value={formData[`${section}_size`]}
                      onChange={handleSliderChange(`${section}_size`)}
                      min={8}
                      max={35}
                      step={1}
                      valueLabelDisplay="auto"
                      sx={{ color: '#1976d2' }}
                    />
                  </Grid>

                <Divider sx={{ my: 5 }} />
              </React.Fragment>
            ))}

            {/* BUTTON SECTION */}
            <Typography variant="h5" fontWeight={700} color="#FF2C6D" mb={2}>
              Button
            </Typography>

            <Grid container spacing={3}>
              {[
                { key: 'button_background', label: 'Background Color' },
                { key: 'button_text_color', label: 'Text Color' },
                { key: 'button_hover_background', label: 'Hover Background' },
                { key: 'button_hover_text_color', label: 'Hover Text Color' },
              ].map(({ key, label }) => (
                <Grid item size={{xs:6, md:6}} key={key}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        border: '3px solid #eee',
                        bgcolor: formData[key],
                        position: 'relative',
                      }}
                    >
                      <input
                        type="color"
                        name={key}
                        value={formData[key]}
                        onChange={handleChange}
                        style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                      />
                    </Box>
                    <Typography variant="subtitle2">{label}</Typography>
                  </Box>
                </Grid>
              ))}

              <Grid item size={{xs:6, md:6}}>
                <FormControl fullWidth size="small">
                  <InputLabel>Style</InputLabel>
                  <Select name="button_style" value={formData.button_style} onChange={handleChange}>
                    <MenuItem value="font-weight:normal !important;">Normal</MenuItem>
                    <MenuItem value="font-weight:bold !important;">Bold</MenuItem>
                    <MenuItem value="font-style:italic !important;">Italic</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item size={{xs:6, md:6}}>
                <FormControl fullWidth size="small">
                  <InputLabel>Text Alignment</InputLabel>
                  <Select
                    name="button_text_direction"
                    value={formData.button_text_direction}
                    onChange={handleChange}
                  >
                    <MenuItem value="text-align:left !important;">Left</MenuItem>
                    <MenuItem value="text-align:center !important;">Center</MenuItem>
                    <MenuItem value="text-align:right !important;">Right</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item size={{xs:6, md:6}}>
                <Typography variant="subtitle2" mb={1}>
                  Padding ({formData.button_padding}px)
                </Typography>
                <Slider
                  value={formData.button_padding}
                  onChange={handleSliderChange('button_padding')}
                  min={8}
                  max={40}
                  step={1}
                  valueLabelDisplay="auto"
                  sx={{ color: '#1976d2' }}
                />
              </Grid>

              <Grid item size={{xs:6, md:6}}>
                <Typography variant="subtitle2" mb={1}>
                  Font Size ({formData.button_text_size}px)
                </Typography>
                <Slider
                  value={formData.button_text_size}
                  onChange={handleSliderChange('button_text_size')}
                  min={8}
                  max={20}
                  step={1}
                  valueLabelDisplay="auto"
                  sx={{ color: '#1976d2' }}
                />
              </Grid>
            </Grid>

            <Box textAlign="right" mt={6}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                sx={{
                  bgcolor: '#0d47a1',
                  '&:hover': { bgcolor: '#002171' },
                  px: 6,
                  py: 1.5,
                }}
                startIcon={saving ? <CircularProgress size={22} color="inherit" /> : <SaveIcon />}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the{" "}
            <strong>
              {fontToDelete === 'font_primary' ? 'Primary' : 'Secondary'} Font
            </strong>
            ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button onClick={confirmDeleteFont} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}